// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
    Button, Card, Field, Input, MessageBar, MessageBarBody, Spinner,
    Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow,
    Title1, Title3, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { usePageTitle } from '../../hooks/usePageTitle';
import { acquireAccessToken } from '../../utils/authToken';
import { CfdiLookupResult, RelatedCfdi, SUPPORTED_ISSUER_RFC, isCfdiUuid, lookupCfdi } from './cfdiLookupService';

const useStyles = makeStyles({
    root: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' },
    card: { display: 'flex', flexDirection: 'column', gap: '12px' },
    form: { display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: '12px' },
    input: { minWidth: '340px', maxWidth: '100%' },
    table: { overflowX: 'auto' },
    uuid: { fontFamily: tokens.fontFamilyMonospace, whiteSpace: 'nowrap' },
});

export default function CfdiLookup() {
    usePageTitle('Mexico CFDI Lookup');
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [uuid, setUuid] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<CfdiLookupResult | null>(null);
    const request = useRef<AbortController | null>(null);

    useEffect(() => () => request.current?.abort(), []);

    async function consult(value: string) {
        if (request.current) return;
        const normalizedUuid = value.trim().toLowerCase();
        setResult(null);
        setError('');
        setUuid(normalizedUuid);
        if (!isCfdiUuid(normalizedUuid)) {
            setError('Enter a valid CFDI UUID in 8-4-4-4-12 format.');
            return;
        }
        const controller = new AbortController();
        request.current = controller;
        setBusy(true);
        try {
            const token = await acquireAccessToken(instance, accounts[0]);
            if (controller.signal.aborted) return;
            if (!token) {
                setError('Sign-in is in progress. Try again after signing in.');
                return;
            }
            const response = await lookupCfdi(normalizedUuid, token, controller.signal);
            if (!controller.signal.aborted) setResult(response);
        } catch (lookupError) {
            if (!controller.signal.aborted) {
                setError(lookupError instanceof Error ? lookupError.message : 'CFDI lookup failed. Contact e-Invoicing support.');
            }
        } finally {
            if (!controller.signal.aborted) setBusy(false);
            request.current = null;
        }
    }

    function relationships(title: string, documents: RelatedCfdi[]) {
        return <Card className={styles.card}>
            <Title3 as="h2">{title} ({documents.length})</Title3>
            {documents.length === 0 ? <Text>No {title.toLowerCase()} returned for this CFDI.</Text> :
                <div className={styles.table}>
                    <Table aria-label={title}>
                        <TableHeader><TableRow>
                            <TableHeaderCell>UUID</TableHeaderCell>
                            <TableHeaderCell>Lookup scope</TableHeaderCell>
                        </TableRow></TableHeader>
                        <TableBody>{documents.map((document, index) => <TableRow key={`${document.uuid}-${index}`}>
                            <TableCell className={styles.uuid}>
                                {document.canLookup === true ?
                                    <Button appearance="transparent" disabled={busy} onClick={() => consult(document.uuid)}
                                        aria-label={`Consult related CFDI ${document.uuid}`}>{document.uuid}</Button> :
                                    <Text>{document.uuid}</Text>}
                            </TableCell>
                            <TableCell>{document.canLookup ? 'Supported issuer' : 'Other/unknown issuer'}</TableCell>
                        </TableRow>)}</TableBody>
                    </Table>
                </div>}
        </Card>;
    }

    return <div className={styles.root}>
        <Title1 as="h1">Mexico CFDI Lookup</Title1>
        <Text>Enter a Mexico CFDI fiscal UUID to check its status, cancellation details and related documents.
            You can also look up older CFDIs that are not listed in this dashboard.</Text>
        <Text>This is a read-only lookup for Microsoft Mexico. Mexico production access with All Partners,
            or dashboard administrator access, is required.</Text>
        <form className={styles.form} onSubmit={event => { event.preventDefault(); void consult(uuid); }}>
            <Field label="CFDI UUID" className={styles.input}>
                <Input value={uuid} disabled={busy} onChange={(_, data) => {
                    setUuid(data.value);
                    setResult(null);
                    setError('');
                }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </Field>
            <Button type="submit" appearance="primary" disabled={busy || !uuid.trim()}>Consult CFDI</Button>
        </form>
        {busy && <Spinner label="Consulting GoSocket..." />}
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
        {result && <>
            <Text>Source: GoSocket / SAT consultation. Retrieved {new Date(result.retrievedAtUtc).toLocaleString()}.
                UUID: {result.uuid}</Text>
            <MessageBar intent="info"><MessageBarBody>
                This view does not cancel, unlink or reprocess invoices. Parent/child relationships do not by themselves
                prove which CFDI blocks cancellation or determine cancellation order. Vendor data may lag SAT.
            </MessageBarBody></MessageBar>
            {result.statusError ?
                <MessageBar intent="warning"><MessageBarBody>Status unavailable: {result.statusError}</MessageBarBody></MessageBar> :
                result.status && <Card className={styles.card}>
                    <Title3 as="h2">CFDI status</Title3>
                    <Text>Document status: {result.status.documentStatus}</Text>
                    <Text>Cancellability: {result.status.cancellability || 'Not returned'}</Text>
                    <Text>Cancellation request status: {result.status.cancellationStatus || 'No cancellation state returned'}</Text>
                    {result.status.documentStatus.toLowerCase() === 'no encontrado' &&
                        <Text>GoSocket did not find this CFDI. This does not establish whether it exists at SAT.</Text>}
                </Card>}
            {result.relationshipsError ?
                <MessageBar intent="warning"><MessageBarBody>Relationships unavailable: {result.relationshipsError}</MessageBarBody></MessageBar> :
                result.relationships && <>
                    {result.relationships.code === '2001' && <Text>GoSocket reported no related CFDIs (code 2001).</Text>}
                    {relationships('Parent CFDIs', result.relationships.parents)}
                    {relationships('Child CFDIs', result.relationships.children)}
                    <Text>Only UUIDs issued by {SUPPORTED_ISSUER_RFC} can be consulted from this page. No automatic traversal or polling is performed.</Text>
                </>}
        </>}
    </div>;
}
